// modal-form-persistence.js

const STORAGE_PREFIX = 'mfp_';
const TRACKED_SELECTOR = '[data-persist-form]';
const registeredForms = new WeakSet();

const TRACKED_TYPES = [
    'text', 'email', 'number', 'tel', 'url', 'search',
    'password', 'date', 'time', 'datetime-local',
    'month', 'week', 'color', 'range',
    'checkbox', 'radio', 'textarea', 'select-one', 'select-multiple'
];

function getFormKey(form) {
    return STORAGE_PREFIX + form.dataset.persistForm;
}

function resolveKey(formOrKey) {
    if (typeof formOrKey === 'string') return STORAGE_PREFIX + formOrKey;
    return getFormKey(formOrKey);
}

function buildFieldKey(field) {
    return field.name || field.id || null;
}

function getFields(form) {
    return [...form.querySelectorAll('input, textarea, select')]
        .filter(f => TRACKED_TYPES.includes(f.type) && buildFieldKey(f));
}

function saveState(form) {
    const key   = getFormKey(form);
    const state = {};

    for (const field of getFields(form)) {
        const fieldKey = buildFieldKey(field);
        if (field.type === 'checkbox' || field.type === 'radio') {
            state[fieldKey] = field.checked;
        } else {
            state[fieldKey] = field.value;
        }
    }

    const hasData = Object.values(state).some(v => v !== '' && v !== false);
    if (hasData) {
        localStorage.setItem(key, JSON.stringify(state));
    } else {
        localStorage.removeItem(key);
    }
}

function restoreState(form) {
    const key = getFormKey(form);
    const raw = localStorage.getItem(key);
    if (!raw) return;

    let state;
    try { state = JSON.parse(raw); } catch { return; }

    for (const field of getFields(form)) {
        const fieldKey = buildFieldKey(field);
        if (!(fieldKey in state)) continue;

        if (field.type === 'checkbox' || field.type === 'radio') {
            field.checked = Boolean(state[fieldKey]);
        } else {
            field.value = state[fieldKey];
        }

        field.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

function attachForm(form) {
    if (registeredForms.has(form)) return;
    registeredForms.add(form);

    if (!form.dataset.persistCleared) {
        restoreState(form);
    }
    delete form.dataset.persistCleared;

    let debounceTimer;
    form.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => saveState(form), 300);
    });
    form.addEventListener('change', () => saveState(form));
}

// Automatická registrace — hlídá DOM mutace
const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
            if (!(node instanceof HTMLElement)) continue;

            if (node.matches(TRACKED_SELECTOR)) {
                attachForm(node);
            }

            node.querySelectorAll(TRACKED_SELECTOR).forEach(attachForm);
        }
    }
});

observer.observe(document.body, { childList: true, subtree: true });

// Statické formy přítomné při načtení stránky
document.querySelectorAll(TRACKED_SELECTOR).forEach(attachForm);

// Public API
export function registerForm(form) {
    attachForm(form);
}

export function clearFormState(formOrKey) {
    localStorage.removeItem(resolveKey(formOrKey));

    // Označ element flagm aby registerForm přeskočil restore
    const el = typeof formOrKey === 'string'
        ? document.querySelector(`[data-persist-form="${formOrKey}"]`)
        : formOrKey;

    if (el) el.dataset.persistCleared = '1';
}

// Globální přístup pro inline skripty
window.registerForm   = registerForm;
window.clearFormState = clearFormState;